/**
 * Platform-administrator Cognitory registry list state for /admin/cogs.
 * Separate from membership-scoped useAuthorizedCognitoryAccess.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchPlatformAdministratorSystems,
  type PlatformAdministratorSystemEntry,
} from "../api/cognitoryPlatformAdminSystemsClient";

export type PlatformAdministratorCognitoriesPhase =
  | "idle_signed_out"
  | "loading"
  | "empty"
  | "one"
  | "many"
  | "unauthorized"
  | "temporary_failure"
  | "unable_to_verify";

export type PlatformAdministratorCognitoriesState = {
  phase: PlatformAdministratorCognitoriesPhase;
  systems: PlatformAdministratorSystemEntry[];
  requestId: string | null;
  retry: () => void;
};

export function usePlatformAdministratorCognitories(input: {
  signedIn: boolean;
}): PlatformAdministratorCognitoriesState {
  const [phase, setPhase] = useState<PlatformAdministratorCognitoriesPhase>(
    input.signedIn ? "loading" : "idle_signed_out",
  );
  const [systems, setSystems] = useState<PlatformAdministratorSystemEntry[]>(
    [],
  );
  const [requestId, setRequestId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!input.signedIn) {
      setPhase("idle_signed_out");
      setSystems([]);
      setRequestId(null);
      return;
    }

    let cancelled = false;
    setPhase("loading");
    setSystems([]);
    setRequestId(null);

    void (async () => {
      const result = await fetchPlatformAdministratorSystems();
      if (cancelled) return;

      setRequestId(result.requestId);
      if (result.kind !== "listed") {
        setSystems([]);
        if (result.kind === "unauthenticated") {
          setPhase("idle_signed_out");
        } else if (result.kind === "forbidden") {
          setPhase("unauthorized");
        } else if (result.kind === "temporary_failure") {
          setPhase("temporary_failure");
        } else {
          setPhase("unable_to_verify");
        }
        return;
      }

      const list = result.systems;
      setSystems(list);
      if (list.length === 0) {
        setPhase("empty");
        return;
      }
      if (list.length === 1) {
        setPhase("one");
        return;
      }
      setPhase("many");
    })();

    return () => {
      cancelled = true;
    };
  }, [input.signedIn, reloadToken]);

  const retry = useCallback(() => {
    setReloadToken(value => value + 1);
  }, []);

  return {
    phase,
    systems,
    requestId,
    retry,
  };
}
